/**
 * Permissions.
 *
 * The system does NOT decide what someone can do from their job title. Every
 * action in the shop is a named permission, and a role is simply a bag of
 * permissions with a name on it. That means:
 *
 *   - the owner can invent roles the original design never imagined
 *     ("Saturday girl", "stockroom", "accountant") without a programmer;
 *   - one person can be given a single extra power without being promoted;
 *   - and the same list drives the menu, the buttons, the approval prompts
 *     and the server's own checks, so they can never disagree.
 *
 * The server is the only place enforcement counts. Hiding a button is a courtesy
 * to the user; the API refuses it regardless.
 */

export type Permission =
  // --- the till ------------------------------------------------------------
  | 'till.sell'
  | 'till.discount'
  | 'till.discount.over_limit'
  | 'till.hold'
  | 'till.price_check'
  | 'till.drawer.open'
  // --- shifts --------------------------------------------------------------
  | 'shift.open'
  | 'shift.close'
  | 'shift.view_all'
  // --- sales ---------------------------------------------------------------
  | 'sales.history'
  | 'sales.reprint'
  | 'sales.void'
  // --- exchanges -----------------------------------------------------------
  | 'exchange.do'
  | 'exchange.outside_window'
  | 'exchange.no_receipt'
  | 'exchange.final_sale'
  | 'exchange.refund_cash'
  // --- store credit and customers ------------------------------------------
  | 'credit.view'
  | 'credit.issue'
  // --- gift cards ----------------------------------------------------------
  | 'giftcard.view'
  | 'giftcard.sell'
  | 'customer.view'
  | 'customer.edit'
  | 'customer.tab'
  // --- catalogue -----------------------------------------------------------
  | 'catalog.view'
  | 'catalog.edit'
  | 'catalog.archive'
  | 'catalog.price_change'
  | 'catalog.barcode'
  | 'promo.manage'
  // --- stock ---------------------------------------------------------------
  | 'stock.view'
  | 'stock.receive'
  | 'stock.adjust'
  | 'stock.count'
  | 'stock.count_apply'
  // --- labels --------------------------------------------------------------
  | 'labels.print'
  | 'labels.design'
  // --- purchases and expenses ----------------------------------------------
  | 'purchase.view'
  | 'purchase.edit'
  | 'expense.view'
  | 'expense.edit'
  // --- reports -------------------------------------------------------------
  | 'reports.dashboard'
  | 'reports.sales'
  | 'reports.profit'
  | 'reports.vat'
  | 'reports.cash'
  | 'reports.inventory'
  | 'reports.export'
  // --- administration ------------------------------------------------------
  | 'settings.view'
  | 'settings.edit'
  | 'settings.currencies'
  | 'users.view'
  | 'users.edit'
  | 'roles.edit'
  | 'devices.view'
  | 'devices.edit'
  | 'audit.view'
  | 'backup.run';

export interface PermissionMeta {
  key: Permission;
  group: PermissionGroup;
  en: string;
  ar: string;
  /** Something a cashier should not hold without the owner thinking about it. */
  sensitive?: boolean;
  /** Can be granted for one action at a time by someone senior at the till. */
  approvable?: boolean;
}

export type PermissionGroup =
  | 'till' | 'shift' | 'sales' | 'exchange' | 'credit' | 'catalog'
  | 'stock' | 'labels' | 'money' | 'reports' | 'admin';

export const PERMISSION_GROUPS: { key: PermissionGroup; en: string; ar: string }[] = [
  { key: 'till', en: 'The till', ar: 'الصندوق' },
  { key: 'shift', en: 'Shifts and cash', ar: 'الورديات والنقد' },
  { key: 'sales', en: 'Sales history', ar: 'سجل المبيعات' },
  { key: 'exchange', en: 'Exchanges and returns', ar: 'الاستبدال والإرجاع' },
  { key: 'credit', en: 'Customers and credit', ar: 'الزبائن والرصيد' },
  { key: 'catalog', en: 'Products', ar: 'الأصناف' },
  { key: 'stock', en: 'Stock', ar: 'المخزون' },
  { key: 'labels', en: 'Labels', ar: 'الملصقات' },
  { key: 'money', en: 'Purchases and expenses', ar: 'المشتريات والمصاريف' },
  { key: 'reports', en: 'Reports', ar: 'التقارير' },
  { key: 'admin', en: 'Setup and security', ar: 'الإعداد والأمان' },
];

export const PERMISSIONS: PermissionMeta[] = [
  { key: 'till.sell', group: 'till', en: 'Sell at the till', ar: 'البيع على الصندوق' },
  { key: 'till.discount', group: 'till', en: 'Give a discount', ar: 'إعطاء خصم' },
  { key: 'till.discount.over_limit', group: 'till', en: 'Discount above the limit', ar: 'خصم أكبر من الحد', sensitive: true, approvable: true },
  { key: 'till.hold', group: 'till', en: 'Hold a sale', ar: 'تعليق فاتورة' },
  { key: 'till.price_check', group: 'till', en: 'Check a price', ar: 'فحص السعر' },
  { key: 'till.drawer.open', group: 'till', en: 'Open the drawer without a sale', ar: 'فتح الدرج بدون بيع', sensitive: true, approvable: true },

  { key: 'shift.open', group: 'shift', en: 'Open a shift', ar: 'فتح وردية' },
  { key: 'shift.close', group: 'shift', en: 'Close a shift', ar: 'إغلاق وردية' },
  { key: 'shift.view_all', group: 'shift', en: 'See everyone’s shifts', ar: 'رؤية ورديات الجميع' },

  { key: 'sales.history', group: 'sales', en: 'See sales history', ar: 'رؤية سجل المبيعات' },
  { key: 'sales.reprint', group: 'sales', en: 'Reprint a receipt', ar: 'إعادة طباعة فاتورة' },
  { key: 'sales.void', group: 'sales', en: 'Cancel a completed sale', ar: 'إلغاء فاتورة مكتملة', sensitive: true, approvable: true },

  { key: 'exchange.do', group: 'exchange', en: 'Do an exchange or return', ar: 'استبدال أو إرجاع' },
  { key: 'exchange.outside_window', group: 'exchange', en: 'Exchange after the time limit', ar: 'استبدال بعد المدة', sensitive: true, approvable: true },
  { key: 'exchange.no_receipt', group: 'exchange', en: 'Take back an item with no receipt', ar: 'إرجاع بدون فاتورة', sensitive: true, approvable: true },
  { key: 'exchange.final_sale', group: 'exchange', en: 'Exchange a final-sale item', ar: 'استبدال صنف بيع نهائي', sensitive: true, approvable: true },
  { key: 'exchange.refund_cash', group: 'exchange', en: 'Give cash back', ar: 'إرجاع نقد', sensitive: true, approvable: true },

  { key: 'credit.view', group: 'credit', en: 'See store credit', ar: 'رؤية رصيد المحل' },
  { key: 'credit.issue', group: 'credit', en: 'Issue store credit by hand', ar: 'إصدار رصيد يدوياً', sensitive: true },
  { key: 'giftcard.view', group: 'credit', en: 'See gift cards', ar: 'رؤية بطاقات الهدايا' },
  // Selling one takes money for goods that leave the shop another day, so it is
  // the same order of trust as issuing credit by hand.
  { key: 'giftcard.sell', group: 'credit', en: 'Sell a gift card', ar: 'بيع بطاقة هدية', sensitive: true },
  { key: 'customer.view', group: 'credit', en: 'See customers', ar: 'رؤية الزبائن' },
  { key: 'customer.edit', group: 'credit', en: 'Add or change a customer', ar: 'إضافة أو تعديل زبون' },
  { key: 'customer.tab', group: 'credit', en: 'Change a customer tab', ar: 'تعديل حساب زبون', sensitive: true },

  { key: 'catalog.view', group: 'catalog', en: 'See products', ar: 'رؤية الأصناف' },
  { key: 'catalog.edit', group: 'catalog', en: 'Add or change a product', ar: 'إضافة أو تعديل صنف' },
  { key: 'catalog.archive', group: 'catalog', en: 'Archive a product', ar: 'أرشفة صنف', sensitive: true },
  { key: 'catalog.price_change', group: 'catalog', en: 'Change a selling price', ar: 'تغيير سعر البيع', sensitive: true },
  { key: 'catalog.barcode', group: 'catalog', en: 'Add a barcode to an item', ar: 'إضافة باركود لصنف' },
  { key: 'promo.manage', group: 'catalog', en: 'Start and stop promotions', ar: 'بدء وإيقاف العروض', sensitive: true },

  { key: 'stock.view', group: 'stock', en: 'See stock levels', ar: 'رؤية المخزون' },
  { key: 'stock.receive', group: 'stock', en: 'Receive a shipment', ar: 'استلام بضاعة' },
  { key: 'stock.adjust', group: 'stock', en: 'Adjust stock', ar: 'تعديل المخزون', sensitive: true },
  { key: 'stock.count', group: 'stock', en: 'Run a stock count', ar: 'عمل جرد' },
  { key: 'stock.count_apply', group: 'stock', en: 'Apply stock count differences', ar: 'تطبيق فروقات الجرد', sensitive: true },

  { key: 'labels.print', group: 'labels', en: 'Print labels', ar: 'طباعة ملصقات' },
  { key: 'labels.design', group: 'labels', en: 'Change the label design', ar: 'تعديل تصميم الملصق' },

  { key: 'purchase.view', group: 'money', en: 'See purchases', ar: 'رؤية المشتريات' },
  { key: 'purchase.edit', group: 'money', en: 'Record a purchase', ar: 'تسجيل مشتريات' },
  { key: 'expense.view', group: 'money', en: 'See expenses', ar: 'رؤية المصاريف' },
  { key: 'expense.edit', group: 'money', en: 'Record an expense', ar: 'تسجيل مصروف' },

  { key: 'reports.dashboard', group: 'reports', en: 'See the dashboard', ar: 'رؤية الصفحة الرئيسية' },
  { key: 'reports.sales', group: 'reports', en: 'See sales reports', ar: 'تقارير المبيعات' },
  { key: 'reports.profit', group: 'reports', en: 'See profit and cost', ar: 'رؤية الأرباح والتكلفة', sensitive: true },
  { key: 'reports.vat', group: 'reports', en: 'See the VAT report', ar: 'تقرير الضريبة' },
  { key: 'reports.cash', group: 'reports', en: 'See cash and shift reports', ar: 'تقارير النقد والورديات' },
  { key: 'reports.inventory', group: 'reports', en: 'See stock value', ar: 'رؤية قيمة المخزون' },
  { key: 'reports.export', group: 'reports', en: 'Export to Excel', ar: 'التصدير إلى إكسل' },

  { key: 'settings.view', group: 'admin', en: 'See settings', ar: 'رؤية الإعدادات' },
  { key: 'settings.edit', group: 'admin', en: 'Change settings', ar: 'تغيير الإعدادات', sensitive: true },
  { key: 'settings.currencies', group: 'admin', en: 'Set exchange rates', ar: 'تحديد أسعار الصرف' },
  { key: 'users.view', group: 'admin', en: 'See staff', ar: 'رؤية الموظفين' },
  { key: 'users.edit', group: 'admin', en: 'Add or change staff', ar: 'إضافة أو تعديل موظف', sensitive: true },
  { key: 'roles.edit', group: 'admin', en: 'Change roles and permissions', ar: 'تعديل الأدوار والصلاحيات', sensitive: true },
  { key: 'devices.view', group: 'admin', en: 'See tills', ar: 'رؤية الصناديق' },
  { key: 'devices.edit', group: 'admin', en: 'Rename a till, or change its receipt prefix', ar: 'تسمية صندوق أو تغيير بادئة فاتورته', sensitive: true },
  { key: 'audit.view', group: 'admin', en: 'See the activity log', ar: 'رؤية سجل العمليات' },
  { key: 'backup.run', group: 'admin', en: 'Make a backup', ar: 'أخذ نسخة احتياطية' },
];

export const ALL_PERMISSIONS: Permission[] = PERMISSIONS.map((p) => p.key);

export const permissionMeta = (key: Permission): PermissionMeta | undefined =>
  PERMISSIONS.find((p) => p.key === key);

/** Permissions a manager can grant for one action, standing at the till. */
export const APPROVABLE: Permission[] = PERMISSIONS.filter((p) => p.approvable).map((p) => p.key);

// ---------------------------------------------------------------------------
// The roles the shop starts with. All three can be edited; none is special-cased
// in code, except that the owner role always holds every permission so the shop
// can never lock itself out.
// ---------------------------------------------------------------------------

export interface RolePreset {
  key: string;
  en: string;
  ar: string;
  builtIn: true;
  /** '*' means every permission there is, now and in future versions. */
  permissions: Permission[] | '*';
  maxDiscountPercent: number;
}

export const CASHIER_PERMISSIONS: Permission[] = [
  'till.sell', 'till.discount', 'till.hold', 'till.price_check',
  'shift.open', 'shift.close',
  'sales.reprint',
  'exchange.do',
  'credit.view', 'customer.view', 'customer.edit',
  'catalog.view', 'stock.view',
  'labels.print',
];

export const MANAGER_PERMISSIONS: Permission[] = [
  ...CASHIER_PERMISSIONS,
  'till.discount.over_limit', 'till.drawer.open',
  'shift.view_all',
  'sales.history', 'sales.void',
  'exchange.outside_window', 'exchange.no_receipt', 'exchange.final_sale',
  'credit.issue', 'customer.tab',
  'catalog.edit', 'catalog.archive', 'catalog.price_change', 'catalog.barcode', 'promo.manage',
  'stock.receive', 'stock.adjust', 'stock.count', 'stock.count_apply',
  'labels.design',
  'purchase.view', 'purchase.edit', 'expense.view', 'expense.edit',
  'reports.dashboard', 'reports.sales', 'reports.profit', 'reports.vat',
  'reports.cash', 'reports.inventory', 'reports.export',
  'settings.view', 'settings.currencies',
  'users.view', 'devices.view', 'audit.view',
];

export const ROLE_PRESETS: RolePreset[] = [
  { key: 'owner', en: 'Owner', ar: 'المالك', builtIn: true, permissions: '*', maxDiscountPercent: 100 },
  { key: 'manager', en: 'Manager', ar: 'مدير', builtIn: true, permissions: MANAGER_PERMISSIONS, maxDiscountPercent: 30 },
  { key: 'cashier', en: 'Cashier', ar: 'كاشير', builtIn: true, permissions: CASHIER_PERMISSIONS, maxDiscountPercent: 10 },
];

export function presetPermissions(key: string): Permission[] {
  const preset = ROLE_PRESETS.find((r) => r.key === key);
  if (!preset) return [];
  return preset.permissions === '*' ? [...ALL_PERMISSIONS] : [...preset.permissions];
}

// ---------------------------------------------------------------------------
// Working out what one person can actually do
// ---------------------------------------------------------------------------

export interface EffectiveGrants {
  /** From the role attached to the user. */
  role: Permission[];
  /** Extra powers given to this one person. */
  granted?: Permission[];
  /** Powers taken away from this one person, even though the role has them. */
  revoked?: Permission[];
  /** The owner role can never be reduced; the shop must stay reachable. */
  isOwnerRole?: boolean;
}

export function effectivePermissions(g: EffectiveGrants): Set<Permission> {
  if (g.isOwnerRole) return new Set(ALL_PERMISSIONS);
  const out = new Set<Permission>(g.role);
  for (const p of g.granted ?? []) out.add(p);
  for (const p of g.revoked ?? []) out.delete(p);
  return out;
}

export function hasPermission(held: Set<Permission> | Permission[], wanted: Permission): boolean {
  return held instanceof Set ? held.has(wanted) : held.includes(wanted);
}

/** Turn a permission the user lacks into the reason shown on the PIN prompt. */
export const APPROVAL_REASON: Partial<Record<Permission, string>> = {
  'till.discount.over_limit': 'approval.reasonOverDiscount',
  'sales.void': 'approval.reasonVoid',
  'exchange.outside_window': 'approval.reasonOutsideWindow',
  'exchange.no_receipt': 'approval.reasonNoReceipt',
  'exchange.final_sale': 'approval.reasonFinalSale',
  'exchange.refund_cash': 'approval.reasonCashRefund',
  'till.drawer.open': 'approval.reasonDrawer',
};
